import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@tyndo/ui-kit';
import { useAuthContext } from '@tyndo/hooks';
import { resolveOidcConfig, completeLogin } from '../auth/oidc';
import { recordError } from '../observability/telemetry';

/**
 * OAuth Authorization Code + PKCE return leg.
 *
 * Keycloak redirects here with `?code=&state=` (or `?error=`). We validate the
 * state nonce (CSRF), exchange the code for tokens using the stored PKCE
 * verifier, store the access token via the auth context, then navigate to the
 * originally requested route. On failure we show a recoverable error with a
 * link back to /login.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setToken } = useAuthContext();
  const [error, setError] = useState<string | null>(null);
  // Guard against React StrictMode double-invoke consuming the one-shot code twice.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      try {
        const config = resolveOidcConfig();
        const { accessToken, returnTo } = await completeLogin(config, window.location.search);
        setToken(accessToken);
        navigate(returnTo, { replace: true });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed';
        setError(message);
        recordError('auth.callback_failed', err, {});
      }
    };

    void run();
  }, [navigate, setToken]);

  return (
    <div className="flex items-center justify-center h-screen bg-surface-base">
      <div className="text-center max-w-md px-6">
        {error ? (
          <>
            <h1 className="text-2xl font-semibold text-foreground mb-2 tracking-tight">
              Sign-in failed
            </h1>
            <p className="text-alarm-critical mb-4 text-sm">{error}</p>
            <Button onClick={() => navigate('/login', { replace: true })}>
              Back to sign in
            </Button>
          </>
        ) : (
          <>
            <div
              role="status"
              aria-label="Completing sign-in"
              className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"
            >
              <span className="sr-only">Completing sign-in…</span>
            </div>
            <p className="text-muted-foreground">Completing sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
